import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Home, Zap, Trophy, Target, Flame, GraduationCap, School, BookOpen, Award, TrendingUp, Lock } from 'lucide-react';

const ChallengerLanding = ({ progress }) => {
  const navigate = useNavigate();

  // Get user info
  const user = progress?.user;
  const userYearGroup = user?.studentProfile?.yearGroup || user?.yearGroup || null;
  const userType = user?.userType;

  // Check if user is admin or superuser
  const isAdminOrSuper = useMemo(() => {
    if (!user) return false;
    return userType === 'admin' || 
           userType === 'superuser' || 
           user?.isAdmin === true ||
           user?.isSuperUser === true ||
           user?.email?.includes('admin@');
  }, [user, userType]);

  // Map user's year group to allowed categories
  const getAllowedCategories = useMemo(() => {
    if (isAdminOrSuper) return null; // null means all categories allowed
    
    if (!userYearGroup) return null;
    
    const yearLower = userYearGroup.toLowerCase().replace(/\s+/g, '-');
    
    const yearToCategoryMap = {
      'year-1': ['seven-plus'],
      'year-2': ['seven-plus'],
      'year 1': ['seven-plus'],
      'year 2': ['seven-plus'],
      'year-3': ['eleven-plus'],
      'year-4': ['eleven-plus'],
      'year 3': ['eleven-plus'],
      'year 4': ['eleven-plus'],
      'year-5': ['eleven-plus'],
      'year-6': ['eleven-plus'],
      'year 5': ['eleven-plus'],
      'year 6': ['eleven-plus'],
      '7plus': ['seven-plus'],
      '7+': ['seven-plus'],
      '11plus': ['eleven-plus'],
      '11+': ['eleven-plus'],
      'year-7': ['ks3'],
      'year-8': ['ks3'],
      'year-9': ['ks3'],
      'year 7': ['ks3'],
      'year 8': ['ks3'],
      'year 9': ['ks3'],
      'year-10': ['gcse'],
      'year-11': ['gcse'],
      'year 10': ['gcse'],
      'year 11': ['gcse'],
      'gcse': ['gcse']
    };
    
    return yearToCategoryMap[yearLower] || yearToCategoryMap[userYearGroup] || null;
  }, [userYearGroup, isAdminOrSuper]);

  const allCategories = [
    {
      id: 'seven-plus',
      title: '7+ Entry',
      description: 'Build foundational skills',
      icon: School,
      color: 'from-pink-500 to-rose-500',
      subjects: [
        { id: 'maths', name: 'Mathematics', difficulty: 'Beginner' },
        { id: 'english', name: 'English', difficulty: 'Beginner' },
        { id: 'reasoning', name: 'Reasoning', difficulty: 'Beginner' }
      ]
    },
    {
      id: 'eleven-plus',
      title: '11+ Entry',
      description: 'Master grammar school exams',
      icon: GraduationCap,
      color: 'from-violet-500 to-purple-500',
      subjects: [
        { id: 'maths-gl', name: 'Maths (GL)', difficulty: 'Intermediate' },
        { id: 'maths-sutton', name: 'Maths (Sutton)', difficulty: 'Intermediate' },
        { id: 'english-gl', name: 'English (GL)', difficulty: 'Intermediate' },
        { id: 'verbal-reasoning', name: 'Verbal Reasoning', difficulty: 'Advanced' },
        { id: 'non-verbal', name: 'Non-Verbal', difficulty: 'Intermediate' }
      ]
    },
    {
      id: 'ks3',
      title: 'Year 9 (KS3)',
      description: 'Strengthen core knowledge',
      icon: BookOpen,
      color: 'from-blue-500 to-cyan-500',
      subjects: [
        { id: 'maths', name: 'Mathematics', difficulty: 'Intermediate' },
        { id: 'english', name: 'English', difficulty: 'Intermediate' },
        { id: 'science', name: 'Science', difficulty: 'Intermediate' }
      ]
    },
    {
      id: 'gcse',
      title: 'GCSE',
      description: 'Exam-level challenges',
      icon: Award,
      color: 'from-emerald-500 to-green-500',
      subjects: [
        { id: 'maths', name: 'Mathematics', difficulty: 'Advanced' },
        { id: 'english', name: 'English', difficulty: 'Advanced' },
        { id: 'science', name: 'Science', difficulty: 'Advanced' }
      ]
    }
  ];

  // Filter categories based on user's access
  const categories = useMemo(() => {
    if (!getAllowedCategories) return allCategories;
    
    return allCategories.map(cat => ({
      ...cat,
      isLocked: !getAllowedCategories.includes(cat.id)
    }));
  }, [getAllowedCategories]);

  // Get the user's primary category for display
  const userCategoryLabel = useMemo(() => {
    if (isAdminOrSuper) return 'Full Access';
    if (!getAllowedCategories) return null;
    
    const primaryCategory = allCategories.find(c => getAllowedCategories.includes(c.id));
    return primaryCategory?.title || userYearGroup;
  }, [getAllowedCategories, isAdminOrSuper, userYearGroup]);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-700 border-green-300';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'Advanced': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="text-white hover:bg-white/20"
                data-testid="back-btn"
              >
                <ArrowLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            </div>
            {userCategoryLabel && (
              <Badge className="bg-white/20 text-white border-white/30">
                <GraduationCap className="w-3 h-3 mr-1" />
                {userCategoryLabel}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-xl">
              <Flame className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Challenger Series</h1>
              <p className="text-white/80 mt-1">
                {isAdminOrSuper 
                  ? 'Full access - Unlimited adaptive practice across all levels'
                  : userYearGroup 
                    ? `Adaptive practice for ${userYearGroup}`
                    : 'Unlimited adaptive practice - questions get harder as you improve'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Banner */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto p-4">
          <div className="flex flex-wrap justify-center gap-6 text-gray-600 text-sm">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-green-600" />
              <span>Adaptive Difficulty</span>
            </div>
            <div className="flex items-center gap-2">
              <Flame className="h-4 w-4 text-orange-600" />
              <span>Build Streaks</span>
            </div>
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-yellow-600" />
              <span>Leaderboards</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <span>Track Progress</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid gap-6">
          {categories.map((category) => {
            const isLocked = category.isLocked;
            
            return (
              <Card 
                key={category.id} 
                className={`overflow-hidden shadow-lg bg-white border-gray-200 ${
                  isLocked ? 'opacity-60' : ''
                }`}
                data-testid={`challenger-category-${category.id}`}
              >
                <CardHeader className={`bg-gradient-to-r ${category.color} text-white ${isLocked ? 'opacity-70' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <category.icon className="h-6 w-6" />
                      <div>
                        <CardTitle className="text-xl flex items-center gap-2">
                          {category.title}
                          {isLocked && <Lock className="w-4 h-4" />}
                        </CardTitle>
                        <CardDescription className="text-white/80">{category.description}</CardDescription>
                      </div>
                    </div>
                    {isLocked && (
                      <Badge className="bg-black/20 text-white/80">
                        Not in your plan
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-4 bg-white">
                  {isLocked ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500 text-sm">
                        This content is for a different year group. Contact support to change your plan.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {category.subjects.map((subject) => (
                        <Button
                          key={subject.id}
                          variant="outline"
                          className="h-auto py-3 px-4 flex flex-col items-start justify-start border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-left bg-white"
                          onClick={() => navigate(`/challenger/${category.id}/${subject.id}`)}
                          data-testid={`challenger-${category.id}-${subject.id}`}
                        >
                          <span className="font-medium text-gray-800">{subject.name}</span>
                          <Badge variant="outline" className={`mt-1 text-xs ${getDifficultyColor(subject.difficulty)}`}>
                            {subject.difficulty}
                          </Badge>
                        </Button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* How It Works */}
        <Card className="mt-8 bg-white border-gray-200 shadow-lg">
          <CardHeader className="border-b border-gray-100">
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <Zap className="h-5 w-5 text-orange-500" />
              How Challenger Mode Works
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl font-bold text-green-600">1</span>
                </div>
                <h3 className="font-semibold text-gray-800 mb-1">Start Easy</h3>
                <p className="text-sm text-gray-600">Begin with foundation questions to build confidence</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl font-bold text-yellow-600">2</span>
                </div>
                <h3 className="font-semibold text-gray-800 mb-1">Level Up</h3>
                <p className="text-sm text-gray-600">Questions adapt to your skill level automatically</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl font-bold text-red-600">3</span>
                </div>
                <h3 className="font-semibold text-gray-800 mb-1">Master It</h3>
                <p className="text-sm text-gray-600">Build streaks and climb the leaderboard</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChallengerLanding;
